import { useEffect, useRef, useState } from 'react';
import * as Y from 'yjs';
import { IndexeddbPersistence } from 'y-indexeddb';
import { Awareness } from 'y-protocols/awareness';
import { supabase } from '../../supabaseClient';
import SupabaseProvider from './collab/SupabaseProvider';
import { u8ToBase64, base64ToU8 } from './collab/yjsBase64';

const SAVE_DEBOUNCE_MS = 1500;
// 버전 스냅샷 throttle — 같은 page 의 마지막 버전이 이 간격 이상 전일 때만 새 스냅샷 insert.
const VERSION_THROTTLE_MS = 10 * 60 * 1000; // 10분
// page_id -> 마지막 버전 시각(ms). 모듈 캐시(불필요한 select 줄임). 0 이면 아직 미확인.
const lastVersionAt = new Map();

/**
 * 버전 스냅샷 throttle 저장.
 * - 마지막 버전이 10분 이상 전일 때만 page_versions insert.
 * - 캐시에 시각이 없으면 DB 에서 1회 확인(select created_at desc limit 1).
 * - 테이블 없음/RLS 등 실패는 try/catch 로 흡수(본 저장 흐름에 영향 없음).
 */
async function maybeSnapshotVersion(pageId, b64, createdBy) {
  try {
    let last = lastVersionAt.get(pageId);
    if (last === undefined) {
      const { data, error } = await supabase
        .schema('workspace').from('page_versions')
        .select('created_at').eq('page_id', pageId)
        .order('created_at', { ascending: false }).limit(1).maybeSingle();
      if (error) return; // 테이블 없음/권한 → 조용히 포기
      last = data && data.created_at ? new Date(data.created_at).getTime() : 0;
      lastVersionAt.set(pageId, last);
    }
    if (Date.now() - last < VERSION_THROTTLE_MS) return; // 아직 throttle 구간
    const { error } = await supabase
      .schema('workspace').from('page_versions')
      .insert({ page_id: pageId, ystate: b64, created_by: createdBy || null });
    if (error) return; // insert 실패해도 본 저장에 영향 없음
    lastVersionAt.set(pageId, Date.now());
  } catch (e) { /* noop — 버전 스냅샷은 best-effort */ }
}

// ---------- 본문 텍스트 미러 (전문검색용 — 마이그레이션 0021) ----------
// ystate 는 Yjs 바이너리라 SQL 검색 불가 → 저장 시 평문을 page_content.text_content 에 함께 기록.
const TEXT_MIRROR_MAX = 100 * 1024; // 평문 상한 100KB
let textMirrorDisabled = false;     // 컬럼 미존재(0021 미적용) 감지 시 세션 내 비활성
let textMirrorWarned = false;       // 콘솔 경고는 1회만
// page_id -> 마지막으로 미러링한 텍스트. 변경 없으면 update 생략(불필요한 요청 절감).
const lastMirroredText = new Map();

/**
 * Y.Doc 의 BlockNote fragment('document-store')에서 평문 추출.
 * XML 직렬화 → 태그 제거 → 공백 정규화. 실패 시 빈 문자열(미러는 best-effort).
 */
function extractDocText(doc) {
  try {
    const frag = doc.getXmlFragment('document-store');
    if (!frag || frag.length === 0) return '';
    let text = frag.toString()
      .replace(/></g, '> <')      // 블록 경계가 붙지 않도록 공백 삽입
      .replace(/<[^>]*>/g, ' ')   // 태그 제거
      .replace(/&lt;/g, '<').replace(/&gt;/g, '>')
      .replace(/&quot;/g, '"').replace(/&amp;/g, '&')
      .replace(/\s+/g, ' ')
      .trim();
    if (text.length > TEXT_MIRROR_MAX) text = text.slice(0, TEXT_MIRROR_MAX);
    return text;
  } catch (e) {
    return '';
  }
}

/**
 * text_content 미러 저장 (best-effort — 본 저장(ystate upsert) 성공 후에만 호출).
 * - 컬럼 미존재(42703/PGRST204) 시 조용히 비활성 + 1회 콘솔 경고(0021 안내).
 * - 그 외 실패는 무시 — 본 저장 흐름에 영향 없음.
 */
async function mirrorTextContent(pageId, doc) {
  if (textMirrorDisabled) return;
  try {
    const text = extractDocText(doc);
    if (lastMirroredText.get(pageId) === text) return; // 변경 없음
    const { error } = await supabase
      .schema('workspace')
      .from('page_content')
      .update({ text_content: text })
      .eq('page_id', pageId);
    if (error) {
      const missing = error.code === '42703' || error.code === 'PGRST204'
        || /text_content/i.test(error.message || '');
      if (missing) {
        textMirrorDisabled = true;
        if (!textMirrorWarned) {
          textMirrorWarned = true;
          // eslint-disable-next-line no-console
          console.warn('[useCollab] page_content.text_content 컬럼 없음 — 본문 검색 미러 저장 건너뜀 (마이그레이션 0021 적용 필요)');
        }
      }
      return;
    }
    lastMirroredText.set(pageId, text);
  } catch (e) { /* noop — 미러 저장은 best-effort */ }
}

// ---------- 내부 페이지 링크 미러 (백링크용 — 마이그레이션 0023) ----------
// 본문 저장 시 문서 안의 /page/<uuid> 내부 링크를 workspace.page_links 에 동기화.
// 테이블 미존재(0023 미적용) 감지 시 세션 내 조용히 비활성 (text_content 미러와 동일 패턴).
let pageLinksDisabled = false;
let pageLinksWarned = false;
// page_id -> 마지막으로 미러링한 target id 목록 직렬화. 변경 없으면 동기화 생략.
const lastMirroredLinks = new Map();

const PAGE_LINK_RE = /\/page\/([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})/gi;

/** page_links 미존재(0023 미적용) 에러인지 판별. */
function isPageLinksMissing(error) {
  if (!error) return false;
  return error.code === '42P01' || error.code === 'PGRST205'
    || /page_links/i.test(error.message || '');
}

/**
 * Y.Doc 의 BlockNote fragment 직렬화(XML)에서 내부 페이지 링크(/page/<uuid>)의
 * uuid 를 전부 추출한다. href 가 절대 URL(https://…/page/<uuid>)이어도 경로 패턴만
 * 보므로 함께 잡힌다. 자기 자신 링크는 제외. 실패 시 빈 배열 (미러는 best-effort).
 */
function extractPageLinks(doc, selfPageId) {
  try {
    const frag = doc.getXmlFragment('document-store');
    if (!frag || frag.length === 0) return [];
    const xml = frag.toString();
    const self = String(selfPageId || '').toLowerCase();
    const ids = new Set();
    let m;
    PAGE_LINK_RE.lastIndex = 0;
    while ((m = PAGE_LINK_RE.exec(xml)) !== null) {
      const id = m[1].toLowerCase();
      if (id !== self) ids.add(id);
    }
    return Array.from(ids).sort();
  } catch (e) {
    return [];
  }
}

/**
 * page_links 동기화 (best-effort — 본 저장(ystate upsert) 성공 후에만 호출).
 * 기존 source rows 전체 delete 후 insert — 문서에서 제거된 링크 잔여분까지 한 번에 정리.
 * - 테이블 미존재 시 조용히 비활성 + 1회 콘솔 경고(0023 안내).
 * - 삭제된 페이지 링크 등으로 FK 위반(23503) 시 행 단위 best-effort 재삽입.
 */
async function mirrorPageLinks(pageId, doc) {
  if (pageLinksDisabled) return;
  try {
    const ids = extractPageLinks(doc, pageId);
    const key = ids.join(',');
    if (lastMirroredLinks.get(pageId) === key) return; // 변경 없음
    const table = () => supabase.schema('workspace').from('page_links');
    const { error: delErr } = await table().delete().eq('source_page_id', pageId);
    if (delErr) {
      if (isPageLinksMissing(delErr)) {
        pageLinksDisabled = true;
        if (!pageLinksWarned) {
          pageLinksWarned = true;
          // eslint-disable-next-line no-console
          console.warn('[useCollab] page_links 테이블 없음 — 백링크 미러 저장 건너뜀 (마이그레이션 0023 적용 필요)');
        }
      }
      return;
    }
    if (ids.length > 0) {
      const rows = ids.map((t) => ({ source_page_id: pageId, target_page_id: t }));
      const { error: insErr } = await table().insert(rows);
      if (insErr) {
        if (insErr.code === '23503') {
          // 존재하지 않는(삭제된) 페이지 링크가 섞임 → 행 단위로 가능한 것만 삽입
          for (const row of rows) {
            // eslint-disable-next-line no-await-in-loop
            await table().insert(row); // 행 단위 실패(error 반환)는 무시 — best-effort
          }
        } else {
          return; // 그 외 실패 — 캐시 갱신 없이 다음 저장 때 재시도
        }
      }
    }
    lastMirroredLinks.set(pageId, key);
  } catch (e) { /* noop — 링크 미러는 best-effort */ }
}

/**
 * 빈 문서(클로버) 감지 — 자동저장이 기존 본문을 빈 문서로 덮어쓰는 데이터 유실을 막는다.
 *
 * 배경(2026-06-29 "Bella OKR" 사고): BlockNote/Yjs 협업 에디터가 드물게 빈 상태로
 * 초기화되는 레이스가 있고, y-prosemirror 가 양방향이라 그 빈 초기화가 공유 문서의
 * 본문을 삭제한 뒤 자동저장이 빈 문서를 page_content 에 upsert → DB 본문이 날아간다.
 * (page_versions 에 "내용 ↔ 빈문서"가 수 분 간격으로 번갈아 쌓인 흔적으로 확인.)
 *
 * 빈 BlockNote 문서 = blockGroup 1개 안의 빈 paragraph 1개. 아래 중 하나라도 맞으면
 * "비어있지 않음"으로 보고 정상 저장한다(오탐으로 정상 저장을 막지 않도록 보수적 판정):
 *  - 텍스트가 하나라도 있음
 *  - 블록(blockContainer)이 2개 이상
 *  - paragraph 가 아닌 블록(이미지·임베드·헤딩·인용 등)이 있음
 */
function isDocEmpty(doc) {
  try {
    const frag = doc.getXmlFragment('document-store');
    if (!frag || frag.length === 0) return true;
    const xml = frag.toString();
    if (xml.replace(/<[^>]*>/g, '').trim().length > 0) return false; // 텍스트 있음
    if ((xml.match(/<blockcontainer/gi) || []).length > 1) return false; // 블록 2개 이상
    if (!/<paragraph/i.test(xml)) return false; // paragraph 외 블록 존재(이미지·임베드 등)
    return true; // 텍스트 0 + 단일 paragraph = 빈 문서
  } catch (e) {
    return false; // 판정 실패 시 정상 저장 허용(편집 불능 방지)
  }
}

/**
 * 다중 루트(blockGroup 2개+) 정규화 — "본문 공백" 버그의 근본 치유 (2026-07-05).
 *
 * 배경: 'document-store' fragment 루트에 blockGroup 이 2개 이상 생기는 오염이 있다.
 * 빈 fragment 로 마운트된 세션(로드 실패/IndexedDB 잔존)이 자기 blockGroup 을 만들고
 * 서버 본문과 병합되면 루트가 2개가 된다. 병합 순서는 clientID 오름차순이라 빈 그룹이
 * 앞에 오면 ProseMirror doc spec(content: blockGroup 정확히 1개)이 첫 그룹만 채택해
 * 화면이 공백이 되고, 이 상태에서 타이핑하면 y-prosemirror write-back 이 뒤의 본문
 * 그룹을 삭제해 서버로 전파한다(isDocEmpty 가드는 "빈 문서"만 막아서 우회됨).
 *
 * 동작: 첫 번째 "본문이 있는" 그룹(전부 비었으면 첫 그룹)을 기준으로, 나머지 그룹들의
 * 비어있지 않은 블록을 Yjs 수준 복제(clone — Y.XmlElement 는 재부착 불가)로 기준 그룹
 * 끝에 이어 붙인 뒤, 기준 외 그룹을 전부 삭제한다. 복제는 변이 전에 끝내므로 복제
 * 단계에서 실패하면 문서는 건드리지 않는다(원본 유지).
 *
 * @returns {boolean} 정규화(변경) 수행 여부
 */
export function normalizeFragmentRoots(doc) {
  try {
    const frag = doc.getXmlFragment('document-store');
    if (!frag || frag.length <= 1) return false; // 정상(0 또는 1개 루트) — no-op

    // 그룹 단위 본문 존재 판정 — isDocEmpty 와 같은 보수적 기준.
    const groupHasContent = (el) => {
      try {
        const xml = el.toString();
        if (xml.replace(/<[^>]*>/g, '').trim().length > 0) return true; // 텍스트 있음
        if ((xml.match(/<blockcontainer/gi) || []).length > 1) return true; // 블록 2개+
        if (el.length > 0 && !/<paragraph/i.test(xml)) return true; // paragraph 외 블록
        return false;
      } catch (e) {
        return true; // 판정 실패 → 본문 있는 것으로 취급(유실 방지)
      }
    };
    // 최상위 블록 단위 판정 — 빈 paragraph 래퍼뿐이면 false(복제 생략).
    const blockHasContent = (node) => {
      try {
        const xml = node.toString();
        if (xml.replace(/<[^>]*>/g, '').trim().length > 0) return true; // 텍스트 있음
        const tags = xml.match(/<([a-zA-Z][a-zA-Z0-9_-]*)/g) || [];
        for (const t of tags) {
          const name = t.slice(1).toLowerCase();
          // 구조 래퍼·빈 paragraph 외의 노드(이미지·임베드·헤딩 등)가 있으면 보존
          if (name !== 'blockcontainer' && name !== 'blockgroup' && name !== 'paragraph') return true;
        }
        return false;
      } catch (e) {
        return true;
      }
    };

    // 기준 그룹: 첫 번째 본문 있는 그룹(전부 비었으면 첫 그룹).
    let baseIndex = 0;
    for (let i = 0; i < frag.length; i += 1) {
      if (groupHasContent(frag.get(i))) { baseIndex = i; break; }
    }
    const base = frag.get(baseIndex);

    // 기준 외 그룹의 비어있지 않은 블록을 전부 복제(변이 전에 완료 — 실패 시 no-op).
    const rescued = [];
    for (let i = 0; i < frag.length; i += 1) {
      if (i === baseIndex) continue;
      const grp = frag.get(i);
      if (!groupHasContent(grp)) continue; // 완전히 빈 여분 그룹 — 삭제만
      for (let j = 0; j < grp.length; j += 1) {
        const child = grp.get(j);
        if (blockHasContent(child)) rescued.push(child.clone());
      }
    }

    doc.transact(() => {
      if (rescued.length > 0) base.insert(base.length, rescued);
      for (let i = frag.length - 1; i >= 0; i -= 1) {
        if (i !== baseIndex) frag.delete(i, 1);
      }
    });
    return true;
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn('[useCollab] 다중 루트 정규화 실패 — 원본 유지:', e && e.message);
    return false;
  }
}

/** 저장 가드용 — 루트에 blockGroup 이 아직 2개 이상 남아있는지(정규화 전/실패 상태). */
function hasMultipleRoots(doc) {
  try {
    const frag = doc.getXmlFragment('document-store');
    return !!frag && frag.length > 1;
  } catch (e) {
    return false; // 판정 실패 시 저장을 막지 않음(편집 불능 방지)
  }
}

/**
 * 서버 스냅샷과 로컬(IndexedDB) 캐시의 Yjs 계보 단절 판정 (2026-07-06).
 *
 * 배경(BUSINESS OS 77c337c6 사고): 서버측 재임포트가 페이지 ystate 를 완전히 새 doc
 * 으로 재작성하면, 그 페이지를 예전에 열었던 브라우저의 IndexedDB 에는 구 계보 doc 이
 * 남는다. 두 doc 이 서로의 clientID 를 전혀 모르는 상태(계보 단절)에서 Yjs 병합을 하면
 * 구+신 콘텐츠가 공존하고 blockGroup 루트가 2개가 되며, multi-root 저장 가드가 저장을
 * 차단해 화면이 낡은 상태로 고착된다. 이 판정으로 그런 캐시를 사전에 걸러낸다.
 *
 * 판정: 서버 update 의 state vector clientID 집합과 로컬 doc 의 state vector clientID
 * 집합의 교집합이 공집합이고, 양쪽 다 편집 이력이 있으면(둘 다 비어있지 않음) 단절.
 *  - 교집합이 하나라도 있으면 같은 계보 — 과거 서버 상태를 본 클라이언트의 미저장
 *    오프라인 편집도 여기 해당하므로 기존 병합 동작을 유지해야 한다(false).
 *  - 로컬이 빈 doc(신규 방문)이거나 서버가 빈 update 면 판정 불가/불필요(false).
 *  - 디코딩 실패 등 판정 불능이면 false(보존) — 캐시 폐기는 파괴적이므로 보수적으로.
 *
 * @param {Uint8Array} serverUpdate 서버 page_content.ystate 의 update 바이트
 * @param {Y.Doc} localDoc IndexedDB 캐시만 로드된 doc (서버 update 적용 전이어야 정확)
 * @returns {boolean} true = 계보 단절(캐시 폐기 대상)
 */
export function isLineageDisjoint(serverUpdate, localDoc) {
  try {
    const serverSV = Y.decodeStateVector(Y.encodeStateVectorFromUpdate(serverUpdate));
    const localSV = Y.decodeStateVector(Y.encodeStateVector(localDoc));
    if (serverSV.size === 0 || localSV.size === 0) return false; // 한쪽이 빈 doc — 단절 아님
    for (const clientId of localSV.keys()) {
      if (serverSV.has(clientId)) return false; // 공유 clientID 존재 — 같은 계보(보존)
    }
    return true; // 교집합 공집합 + 양쪽 다 이력 있음 — 계보 단절
  } catch (e) {
    return false; // 판정 실패 시 보존(파괴적 폐기는 확실할 때만)
  }
}

/**
 * IndexedDB 데이터베이스 삭제 (계보 단절 캐시 폐기용 — PageEditor 버전 복원 선례와 동일
 * 대상 `root-page-<pageId>`).
 *
 * @returns {Promise<boolean>} 삭제 확정 여부. 다른 탭이 같은 DB 를 열고 있으면(blocked)
 *   false — blocked 중에 다시 open 하면 삭제 완료까지 open 이 큐잉되어 whenSynced 가
 *   영원히 안 풀릴 수 있으므로, 호출측은 false 면 이 세션의 IndexedDB 사용을 포기하고
 *   메모리 전용으로 진행해야 한다.
 */
function deleteIndexeddbDatabase(name) {
  return new Promise((resolve) => {
    try {
      if (typeof indexedDB === 'undefined' || !indexedDB.deleteDatabase) {
        resolve(true); // IndexedDB 자체가 없는 환경 — 폐기할 캐시도 없음
        return;
      }
      const req = indexedDB.deleteDatabase(name);
      req.onsuccess = () => resolve(true);
      req.onerror = () => resolve(false);   // 삭제 실패 — stale 캐시가 남아있으니 미사용
      req.onblocked = () => resolve(false); // 다른 탭이 열어둠 — 대기하면 행 걸림
    } catch (e) {
      resolve(false);
    }
  });
}

/**
 * 한 페이지에 대한 협업 세션을 구성한다.
 * - Y.Doc + IndexedDB(오프라인) + Supabase 스냅샷 로드 + Realtime 프로바이더.
 * 반환: { doc, provider, awareness, ready }
 */
export default function useCollab(pageId, userMeta, createdBy) {
  const [bundle, setBundle] = useState(null);
  const userRef = useRef(userMeta);
  userRef.current = userMeta;
  // 버전 스냅샷 작성자(created_by) — 최신 값 ref 추적.
  const createdByRef = useRef(createdBy);
  createdByRef.current = createdBy;

  useEffect(() => {
    if (!pageId) return undefined;
    let destroyed = false;
    let saveTimer = null;
    // 서버 스냅샷 로드 실패(재시도 포함) 시 true — 이 세션의 모든 저장(디바운스·unmount)
    // 을 전면 비활성한다. 빈 문서로 편집을 시작해 서버 본문을 덮어쓰는 사고 방지.
    let loadFailed = false;

    const doc = new Y.Doc();
    const awareness = new Awareness(doc);
    // IndexedDB persistence 는 stale 캐시 판정(계보 단절) 후에만 본 doc 에 붙인다 —
    // y-indexeddb 는 생성 즉시 로드를 시작해서, 먼저 붙이면 구 계보 캐시가 doc 에
    // 병합돼 버려 서버 상태만으로 fresh 시작이 불가능해진다. 아래 async 흐름 (2) 참조.
    let persistence = null;
    let provider = null;

    const saveNow = async () => {
      if (destroyed) return;
      // 로드 실패 세션은 저장 전면 금지 — 서버 본문을 못 읽은 상태의 doc 을
      // upsert 하면 정상 본문을 빈/불완전 문서로 덮어쓴다.
      if (loadFailed) {
        // eslint-disable-next-line no-console
        console.warn('[useCollab] 로드 실패 세션 — 저장 건너뜀(읽기 전용):', pageId);
        return;
      }
      // 다중 blockGroup 루트가 남아있으면(정규화 전/실패) 저장 금지 — 이 상태의
      // write-back(뒤 본문 그룹 삭제)이 서버로 영속화되는 것을 막는다.
      if (hasMultipleRoots(doc)) {
        // eslint-disable-next-line no-console
        console.warn('[useCollab] 다중 blockGroup 루트 감지 — 저장 건너뜀(정규화 필요):', pageId);
        return;
      }
      // ⚠️ 클로버 방지(2026-06-29 Bella OKR 사고): 빈 문서면 자동저장을 건너뛴다.
      //    협업 초기화 레이스로 doc 이 비어버려도 DB 의 정상 본문을 덮어쓰지 않게 한다.
      if (isDocEmpty(doc)) {
        // eslint-disable-next-line no-console
        console.warn('[useCollab] 빈 문서 감지 — 자동저장 건너뜀(클로버 방지):', pageId);
        return;
      }
      const b64 = u8ToBase64(Y.encodeStateAsUpdate(doc));
      const { error } = await supabase
        .schema('workspace')
        .from('page_content')
        .upsert(
          { page_id: pageId, ystate: b64, updated_at: new Date().toISOString() },
          { onConflict: 'page_id' }
        );
      // 본 저장 성공 시에만 버전 스냅샷 throttle + 전문검색용 평문 미러 + 백링크 링크 미러 시도.
      if (!error) {
        maybeSnapshotVersion(pageId, b64, createdByRef.current);
        mirrorTextContent(pageId, doc);
        mirrorPageLinks(pageId, doc);
      }
    };
    const scheduleSave = () => {
      if (saveTimer) clearTimeout(saveTimer);
      saveTimer = setTimeout(saveNow, SAVE_DEBOUNCE_MS);
    };

    (async () => {
      // 0) 로컬(IndexedDB) 캐시 계보 probe — 본 doc 오염 없이 임시 doc 에만 캐시를
      //    로드해 state vector 를 읽는다(서버 fetch 와 병렬 — 로드 지연 최소화).
      //    본 doc 에 먼저 붙이면 서버 update 와 뒤섞여 "로컬만의 계보"를 알 수 없다.
      const probeDoc = new Y.Doc();
      const probePromise = (async () => {
        try {
          const probe = new IndexeddbPersistence(`root-page-${pageId}`, probeDoc);
          await probe.whenSynced;
          await probe.destroy(); // DB 연결 닫기 — 이후 deleteDatabase/재open 이 안 막히게
        } catch (e) { /* noop — probe 실패 시 겹침(보존)으로 취급 */ }
      })();

      // 1) 서버 스냅샷 로드
      //    ⚠️ error 침묵 금지(2026-07-05 본문 공백 버그): 일시 오류를 무시하면 빈 문서로
      //    편집이 시작되고, 자동저장이 서버의 정상 본문을 덮어쓴다. 실패 시 1회 재시도,
      //    재실패 시 loadFailed — 이 세션은 읽기만 가능(모든 저장 비활성).
      const fetchSnapshot = () => supabase
        .schema('workspace')
        .from('page_content')
        .select('ystate')
        .eq('page_id', pageId)
        .maybeSingle();
      let { data, error } = await fetchSnapshot();
      if (error && !destroyed) {
        ({ data, error } = await fetchSnapshot()); // 일시 오류(네트워크 등) 대비 1회 재시도
      }
      let serverU8 = null; // 서버 ystate 바이트 — 계보 판정 후 (3) 에서 doc 에 적용
      if (error) {
        loadFailed = true;
        // eslint-disable-next-line no-console
        console.warn('[useCollab] 서버 스냅샷 로드 실패(재시도 포함) — 이 세션 저장 비활성(읽기 전용):', pageId, error.message || error);
      } else if (data && data.ystate) {
        try {
          serverU8 = base64ToU8(data.ystate);
        } catch (e) {
          // 스냅샷이 있는데 디코딩 실패 → 빈 문서로 편집 시작 금지(로드 실패와 동일 처리)
          loadFailed = true;
          // eslint-disable-next-line no-console
          console.warn('[useCollab] ystate 적용 실패 — 이 세션 저장 비활성(읽기 전용):', pageId, e && e.message);
        }
      }
      // data == null (행 없음) 은 새 페이지 — 정상 진행.

      // 2) stale 캐시 자동 폐기 가드 — 서버 doc 과 로컬 캐시의 계보 단절 시 캐시 폐기.
      //    loadFailed(서버 상태 미확보) 면 판정 불가 → 기존 캐시 유지(읽기 전용 세션).
      await probePromise;
      if (destroyed) { probeDoc.destroy(); return; }
      let idbUsable = true; // false = 이 세션은 IndexedDB 미사용(메모리 전용)
      if (!loadFailed && serverU8 && isLineageDisjoint(serverU8, probeDoc)) {
        // eslint-disable-next-line no-console
        console.warn('[루트/collab] 로컬 캐시 계보 단절 감지 — 캐시 폐기 후 서버 상태로 재동기화:', pageId);
        idbUsable = await deleteIndexeddbDatabase(`root-page-${pageId}`);
        if (!idbUsable) {
          // 다른 탭이 DB 를 열고 있는 등 삭제 미확정 — stale 캐시 재병합을 막기 위해
          // 이 세션은 IndexedDB 없이(서버 + Realtime 만) 진행.
          // eslint-disable-next-line no-console
          console.warn('[루트/collab] 캐시 폐기 미확정(다른 탭 사용 중?) — 이 세션 IndexedDB 미사용:', pageId);
        }
      }
      probeDoc.destroy();
      if (destroyed) return;

      // 3) 서버 스냅샷을 doc 에 적용
      if (serverU8) {
        try {
          Y.applyUpdate(doc, serverU8);
        } catch (e) {
          // 적용 실패 → 빈 문서로 편집 시작 금지(로드 실패와 동일 처리)
          loadFailed = true;
          // eslint-disable-next-line no-console
          console.warn('[useCollab] ystate 적용 실패 — 이 세션 저장 비활성(읽기 전용):', pageId, e && e.message);
        }
      }

      // 4) 로컬(IndexedDB) 부착 + 동기 대기 — 폐기 판정이 끝난 뒤에만 본 doc 에 붙인다.
      //    같은 계보(교집합 있음)의 오프라인 편집은 여기서 기존처럼 병합·보존된다.
      if (idbUsable) {
        try {
          persistence = new IndexeddbPersistence(`root-page-${pageId}`, doc);
          await persistence.whenSynced;
        } catch (e) { /* noop */ }
      }
      if (destroyed) return;

      // 5) 다중 blockGroup 루트 정규화 — 서버+IndexedDB 병합이 끝난 시점, 에디터
      //    바인딩(y-prosemirror) 전에 치유해야 write-back 의 본문 삭제를 원천 차단.
      //    (계보 단절 캐시는 (2) 에서 이미 폐기 — 여기는 같은 계보 병합의 잔여 오염 치유.)
      const normalized = normalizeFragmentRoots(doc);
      if (normalized) {
        // eslint-disable-next-line no-console
        console.warn('[useCollab] 다중 blockGroup 루트 정규화 수행:', pageId);
      }

      // 6) 내 awareness 설정
      const u = userRef.current || {};
      awareness.setLocalStateField('user', { name: u.name || '익명', color: u.color || '#2383e2' });

      // 7) Realtime 프로바이더 시작
      provider = new SupabaseProvider(supabase, pageId, doc, awareness);

      // 8) 변경 시 서버 저장(디바운스)
      doc.on('update', scheduleSave);

      // 정규화가 일어났으면 치유 결과 즉시 영속화(1회) — saveNow 내부 가드
      // (loadFailed/빈 문서)는 그대로 적용된다.
      if (normalized) saveNow();

      setBundle({ doc, provider, awareness, loadFailed });
    })();

    return () => {
      destroyed = true;
      if (saveTimer) clearTimeout(saveTimer);
      try { doc.off('update', scheduleSave); } catch (e) { /* noop */ }
      // 마지막 상태 best-effort 저장 — 로드 실패 세션·다중 루트·빈 문서면 건너뜀
      // (클로버/오염 전파 방지 — saveNow 와 동일 가드).
      try {
        if (!loadFailed && !hasMultipleRoots(doc) && !isDocEmpty(doc)) {
          const b64 = u8ToBase64(Y.encodeStateAsUpdate(doc));
          supabase.schema('workspace').from('page_content')
            .upsert({ page_id: pageId, ystate: b64, updated_at: new Date().toISOString() }, { onConflict: 'page_id' });
        }
      } catch (e) { /* noop */ }
      if (provider) provider.destroy();
      try { if (persistence) persistence.destroy(); } catch (e) { /* noop */ }
      try { awareness.destroy(); } catch (e) { /* noop */ }
      try { doc.destroy(); } catch (e) { /* noop */ }
      setBundle(null);
    };
  }, [pageId]);

  // 협업 커서 라벨용 사용자 정보(name/color) 를 awareness 에 최신으로 유지.
  // - 초기값은 위 effect 의 (3) 에서 세팅되지만, 멤버 프로필(display_name)이
  //   마운트 이후 늦게 로드되면 라벨이 이메일/'익명' 으로 남는다 → 여기서 갱신.
  // - deps 는 문자열(name/color)로 — userMeta 객체가 렌더마다 새로 만들어져도
  //   값이 같으면 재실행/브로드캐스트 spam 이 없다.
  const uName = userMeta && userMeta.name;
  const uColor = userMeta && userMeta.color;
  useEffect(() => {
    if (!bundle || !bundle.awareness) return;
    const cur = (bundle.awareness.getLocalState() || {}).user || {};
    const next = { name: uName || '익명', color: uColor || '#2383e2' };
    if (cur.name === next.name && cur.color === next.color) return;
    bundle.awareness.setLocalStateField('user', next);
  }, [bundle, uName, uColor]);

  return bundle;
}
